import { Kicker } from '@developerz/ui';
import { A } from '@solidjs/router';
import { createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { adminOrgHref, type AdminUserDetail as UserDetail } from '../lib/admin';
import { grantSubjectHref } from '../lib/adminGrants';
import { absolute } from '../lib/time';
import { AdminMemberRole } from './AdminMemberRole';
import { AdminUserGrants } from './AdminUserGrants';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';

// One person's admin page (`/admin/users/:id`, #5727) — presenter. Each
// membership carries the staff role control (`<AdminMemberRole/>`, on an
// `admin:accounts` operator credential, #5724); its success is `onChanged`, the
// container's re-read.
//
// Laid out as `<AdminOrgDetail/>` is: the identity as the shared `.facts` list,
// then one section heading per `row-list`, "none" said by `<EmptyState/>`. A
// membership links to that org's detail on `/admin/orgs`; grants link to the
// subject-addressed grants page, as the registry row's Grant does.

export function AdminUserDetail(props: {
  user: UserDetail;
  token: string | null;
  onChanged: () => void;
}): JSX.Element {
  const approved = (): string | null => props.user.approved_at_iso;
  const membershipsId = createUniqueId();
  return (
    <Panel title={props.user.login}>
      <div class="admin-detail">
        <dl class="facts">
          <div>
            <dt>{t('admin.user_field_id')}</dt>
            <dd>
              <code>{props.user.id}</code>
            </dd>
          </div>
          <div>
            <dt>{t('admin.user_field_email')}</dt>
            <dd>{props.user.email ?? t('admin.user_no_email')}</dd>
          </div>
          <div>
            <dt>{t('admin.user_field_access')}</dt>
            <dd class="admin-row__stack">
              <StatusPill tone={approved() !== null ? 'success' : 'warning'}>
                {approved() !== null ? t('admin.user_approved') : t('admin.user_waitlisted')}
              </StatusPill>
              <Show when={approved()}>
                {(at) => (
                  <span class="admin-row__meta">
                    {t('admin.user_approved_since', { at: absolute(at()) })}
                  </span>
                )}
              </Show>
            </dd>
          </div>
          <div>
            <dt>{t('admin.user_field_joined')}</dt>
            <dd>{absolute(props.user.created_at_iso)}</dd>
          </div>
          <div>
            <dt>{t('admin.user_field_last_active')}</dt>
            <dd>
              {props.user.last_active_iso !== null
                ? absolute(props.user.last_active_iso)
                : t('admin.user_never_active')}
            </dd>
          </div>
        </dl>

        <h3 id={membershipsId}>
          <Kicker as="span">{t('admin.user_memberships_heading')}</Kicker>
        </h3>
        <Show
          when={props.user.memberships.length > 0}
          fallback={<EmptyState message={t('admin.user_memberships_empty')} />}
        >
          <ul class="row-list" aria-labelledby={membershipsId}>
            <For each={props.user.memberships}>
              {(m) => (
                <li class="row-list__row">
                  <A class="row-list__lead" href={adminOrgHref(m.account_id)}>
                    {m.github_login}
                  </A>
                  <div class="admin-row__stack">
                    <AdminMemberRole
                      accountId={m.account_id}
                      userId={props.user.id}
                      login={props.user.login}
                      org={m.github_login}
                      memberRole={m.role}
                      token={props.token}
                      onChanged={props.onChanged}
                    />
                    <Show when={m.kind === 'user'}>
                      <span class="admin-row__meta">{t('admin.user_membership_personal')}</span>
                    </Show>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>

        <AdminUserGrants
          grants={props.user.grants}
          href={grantSubjectHref({ kind: 'user', id: props.user.id, label: props.user.login })}
        />
      </div>
    </Panel>
  );
}
